/**
 * F-design — ErpShell: frame global (sidebar 248px + topbar) recriado a partir
 * do handoff "design_handoff_gestion_erp" (v2 SOLID). Substitui, gradualmente,
 * os headers por página.
 *
 * Wiring real:
 *  - Nav agrupada por IA → rotas reais do wouter, labels i18n (FR/PT/EN).
 *  - Seletor de empresa → useSelectedCompany (localStorage) + reload total
 *    para re-escopar os dados de todas as páginas.
 *  - FR/PT/EN → <LanguageSwitcher> existente (sincroniza com o backend).
 *  - Toggle 👁 leitura → reflete o papel (useIsReadOnly); editores podem
 *    ativar um "preview" de leitura. Aplica `is-readonly` no <body>.
 *  - Rodapé → usuário + papel + logout.
 *
 * Itens de Finance/Usuários só aparecem para super admin.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { setPin, usePin } from "../../hooks/usePin.js";
import {
  AlertTriangle,
  BarChart3,
  Bell,
  CalendarDays,
  Car,
  ChevronDown,
  ClipboardCheck,
  CloudHail,
  Coins,
  Eye,
  FileSignature,
  FileSpreadsheet,
  FilePen,
  FileText,
  FolderOpen,
  HandCoins,
  Handshake,
  HardHat,
  Landmark,
  LayoutDashboard,
  LineChart,
  ListChecks,
  LogOut,
  Megaphone,
  Menu,
  Moon,
  Percent,
  Receipt,
  ReceiptText,
  Scale,
  Search,
  Send,
  Settings,
  ShoppingCart,
  Sun,
  Upload,
  Users,
  Wrench,
  X,
  type LucideIcon,
} from "lucide-react";
import { formatMoney } from "../../lib/format-fr.js";
import { LanguageSwitcher } from "../common/LanguageSwitcher.js";
import { useTheme } from "../theme/ThemeProvider.js";
import { useAuth } from "../../hooks/useAuth.js";
import { TeamNoticesPopup } from "../domain/TeamNoticesPopup.js";
import { useGrantOnly } from "../../hooks/useGrantOnly.js";
import { useIsReadOnly } from "../../hooks/useCanEdit.js";
import { useSelectedCompany } from "../../hooks/useSelectedCompany.js";
import { clearImpersonationToken } from "../../lib/impersonation.js";
import { useT, type MessageKey } from "../../i18n/I18nProvider.js";
import type { Locale } from "../../i18n/messages.js";
import { trpc } from "../../lib/trpc.js";
import "../../styles/erp-shell.css";

interface NavItem {
  id: string;
  icon: LucideIcon;
  href: string;
  /** Chave i18n (preferida) ou label literal (para itens sem tradução ainda). */
  labelKey?: MessageKey;
  label?: string;
  amber?: boolean;
  /** Só visível para super admin. */
  superAdmin?: boolean;
  /** F-recados-equipe — super admin OU Administrador em ≥1 empresa. */
  adminOnly?: boolean;
  /** F-tarefas — visível para TODOS (inclusive gerente só com menus liberados). */
  everyone?: boolean;
  /**
   * F-finance-viewer-access: item do módulo financeiro (Tesouraria) — visível a
   * super admin OU papel `viewer` (sócio) em ≥1 empresa; admin/user comuns não.
   */
  financeAccess?: boolean;
  /** F-menu-unico — rótulo curto no trilho (72 px); default = o rótulo normal. */
  railLabelKey?: MessageKey;
}
interface NavGroup {
  sectionKey?: MessageKey;
  section?: string;
  items: NavItem[];
}

/**
 * F-menu-unico (auditoria UX 23/10/2026, item 2 — brief "Home do Gestion V3",
 * fase 3; CEO 24/10: "pode seguir"). Antes o menu tinha dois modos escondidos
 * atrás do botão "Operação / Finanças" (metade dos itens invisível, "Faturas"
 * nos dois). Agora: UM menu, grupos pelo tipo de trabalho, cada item em um
 * lugar só; um trilho de 72 px com os destinos de todo dia (RAIL_IDS) e a
 * gaveta com o resto.
 */
const NAV: ReadonlyArray<NavGroup> = [
  {
    items: [
      {
        id: "dashboard",
        icon: LayoutDashboard,
        href: "/dashboard",
        labelKey: "nav.dashboardTitle",
      },
      // F-tarefas (CEO 2026-10-29) — "Minhas tarefas" para todos; Admin cria.
      { id: "tarefas", icon: ListChecks, href: "/tarefas", labelKey: "nav.tasks", everyone: true },
    ],
  },
  // CEO 2026-11-01 ("onde está o menu clientes… estranho e não intuitivo") —
  // grupos na ordem do trabalho: operação → clientes e equipe → faturação →
  // banco → relatórios → ferramentas. Clientes sobe para junto dos técnicos
  // e entra no trilho; o banco fica todo num grupo só (inclusive as contas);
  // importação eDSP e caixa de saída vão para Ferramentas.
  {
    sectionKey: "nav.sectionOperations",
    items: [
      { id: "dossiers", icon: FolderOpen, href: "/dossiers", labelKey: "nav.dossiers" },
      // F-devis-list — o funil ANTES do dossier: nem todo devis vira reparo.
      { id: "devis", icon: FileText, href: "/devis", labelKey: "erpShell.navDevis" },
      { id: "chantiers", icon: HardHat, href: "/chantiers", labelKey: "erpShell.navChantiers" },
      { id: "agenda", icon: CalendarDays, href: "/agenda", labelKey: "erpShell.navAgenda" },
      { id: "parc-grele", icon: CloudHail, href: "/parc-grele", labelKey: "erpShell.navParcGrele" },
    ],
  },
  {
    sectionKey: "nav.sectionPeople",
    items: [
      { id: "clientes", icon: Users, href: "/clientes", labelKey: "nav.customers" },
      { id: "tecnicos", icon: Wrench, href: "/tecnicos", labelKey: "nav.technicians" },
      {
        id: "intermediarios",
        icon: Handshake,
        href: "/intermediarios",
        labelKey: "erpShell.navIntermediaries",
      },
      { id: "fornecedores", icon: ShoppingCart, href: "/fornecedores", labelKey: "nav.suppliers" },
    ],
  },
  {
    sectionKey: "nav.sectionBilling",
    items: [
      { id: "faturas", icon: Receipt, href: "/faturas", labelKey: "nav.faturas" },
      { id: "proformas", icon: FileSignature, href: "/proformas", labelKey: "nav.proformas" },
      {
        id: "pagamentos",
        icon: HandCoins,
        href: "/pagamentos-tecnicos",
        labelKey: "shell.technicianPayments",
      },
    ],
  },
  {
    sectionKey: "nav.sectionFinance",
    items: [
      {
        id: "banco",
        icon: Landmark,
        href: "/banco",
        labelKey: "nav.bankExtracts",
        railLabelKey: "nav.sectionBank",
      },
      // CEO 2026-10-19: a página só se achava pelo link dentro de Empresas.
      {
        id: "contasBancarias",
        icon: Landmark,
        href: "/contas-bancarias",
        labelKey: "erpShell.navBankAccounts",
      },
      { id: "compras", icon: ReceiptText, href: "/compras", labelKey: "nav.purchaseInvoices" },
      { id: "socio", icon: Coins, href: "/banco/socio", labelKey: "nav.partnerAccount" },
      {
        id: "financeiro",
        icon: LineChart,
        href: "/financeiro",
        labelKey: "nav.financeKpis",
        financeAccess: true,
      },
    ],
  },
  {
    sectionKey: "nav.sectionFiscalReports",
    items: [
      {
        id: "relatorio",
        icon: BarChart3,
        href: "/relatorio",
        labelKey: "shell.reportConsolidated",
      },
      { id: "gap-ca", icon: Scale, href: "/relatorio-ca", labelKey: "erpShell.navCaGap" },
      { id: "controle", icon: LineChart, href: "/controle", labelKey: "nav.controle" },
      { id: "fiscal-sig", icon: FileSpreadsheet, href: "/fiscal/sig", labelKey: "nav.fiscalSig" },
      { id: "fiscal-seuil", icon: Percent, href: "/fiscal/seuil", labelKey: "nav.fiscalSeuil" },
      { id: "fiscal-tva", icon: FileSpreadsheet, href: "/fiscal/tva", labelKey: "nav.fiscalTva" },
    ],
  },
  {
    sectionKey: "nav.sectionAdmin",
    items: [
      { id: "import", icon: Upload, href: "/import/edsp", labelKey: "nav.importEdsp" },
      { id: "emails", icon: Send, href: "/emails", labelKey: "erpShell.navEmailOutbox" },
      // F-recados-equipe (CEO 2026-10-28) — recados em pop-up para a equipe.
      {
        id: "recados",
        icon: Megaphone,
        href: "/recados",
        labelKey: "nav.teamNotices",
        adminOnly: true,
      },
      // CEO 2026-11-01 — o cadastro de veículos (ficha por placa) tinha o mesmo
      // nome da lista de carros em francês ("Véhicules"); vira "Cadastro".
      { id: "veiculos", icon: Car, href: "/veiculos", labelKey: "nav.vehicles" },
      { id: "usuarios", icon: Users, href: "/usuarios", labelKey: "nav.users", adminOnly: true },
      { id: "empresas", icon: Settings, href: "/empresas", labelKey: "nav.companiesAdmin" },
      { id: "audit", icon: ClipboardCheck, href: "/audit", labelKey: "nav.auditLog" },
    ],
  },
];

/** Destinos de todo dia, no trilho (ordem = ordem do trilho). CEO 2026-11-01: + Clientes. */
const RAIL_IDS = ["dashboard", "dossiers", "clientes", "chantiers", "faturas", "banco"] as const;

/** Pessoa: primeiro + último nome ("Landione Souza" → LS). */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/**
 * Empresa: as DUAS PRIMEIRAS palavras ("Euro Storm Débosselage" → ES,
 * "Brazilian Storms PDR Service OÜ" → BS). CEO 2026-10-24: a regra de pessoa
 * (primeira + última) dava "ED" e "BO".
 */
function companyInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

const COUNTRY_NAMES: Record<string, string> = {
  FR: "France",
  BR: "Brésil",
  EE: "Estonie",
  PT: "Portugal",
};

function companySublabel(
  c: { countryCode: string; isPrimary?: boolean },
  t: (k: MessageKey) => string,
): string {
  const country = COUNTRY_NAMES[c.countryCode] ?? c.countryCode;
  return `${country} · ${c.isPrimary ? t("shell.companyPrimary") : t("shell.companyGroup")}`;
}

/**
 * Deriva o id do item de nav ativo a partir do pathname: match exato primeiro,
 * senão o item cujo href é o prefixo mais longo (cobre rotas de detalhe como
 * /dossiers/:id → "dossiers", /banco/socio → "socio").
 */
function deriveActive(pathname: string): string | undefined {
  const all = NAV.flatMap((g) => g.items);
  const exact = all.find((it) => it.href === pathname);
  if (exact) return exact.id;
  const byPrefix = all
    .filter((it) => it.href !== "/" && pathname.startsWith(it.href))
    .sort((a, b) => b.href.length - a.href.length)[0];
  return byPrefix?.id;
}

export interface ErpShellProps {
  /** id do item de nav ativo (ver NAV). Default: derivado da rota atual. */
  active?: string;
  /** Partes do breadcrumb (a última é destacada). Default: o label do item ativo. */
  crumb?: string[];
  children: React.ReactNode;
}

export function ErpShell({ active: activeProp, crumb, children }: ErpShellProps) {
  const t = useT();
  const [location, setLocation] = useLocation();
  const active = activeProp ?? deriveActive(location);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  // F-acesso-por-menu (CEO 2026-10-12) — gerente de chantier com menus
  // liberados: menu lateral só com eles, sem Finanças, sem sininho de caixa.
  const { grantOnly, menus: menusLiberados } = useGrantOnly();

  // F-recados-equipe (CEO 2026-10-28) — recados do escritório: pop-up ao
  // entrar (uma vez por montagem) + itens no sininho.
  const teamNoticesQ = trpc.erpNotices.mine.useQuery(undefined, {
    enabled: !!user,
    refetchInterval: 5 * 60_000,
  });
  const teamNotices = teamNoticesQ.data?.rows ?? [];
  // F-tarefas (CEO 2026-10-29) — aviso só no sino e no Painel.
  const myTasksQ = trpc.tasks.mine.useQuery(undefined, {
    enabled: !!user,
    refetchInterval: 5 * 60_000,
  });
  // F-tarefas-popup (CEO 2026-10-29) — tarefas atrasadas/de hoje abrem o
  // mesmo pop-up ao entrar; "Ver depois" esconde até a próxima entrada.
  const popupTasks = (myTasksQ.data?.rows ?? []).filter((r) => r.popup);
  const [noticesOpen, setNoticesOpen] = useState(false);
  const [noticesAutoOpened, setNoticesAutoOpened] = useState(false);
  useEffect(() => {
    if (!noticesAutoOpened && (teamNotices.length > 0 || popupTasks.length > 0)) {
      setNoticesOpen(true);
      setNoticesAutoOpened(true);
    }
  }, [teamNotices.length, popupTasks.length, noticesAutoOpened]);

  const companiesQuery = trpc.companies.list.useQuery();
  const companies = useMemo(() => companiesQuery.data ?? [], [companiesQuery.data]);
  const [companyId, setCompanyId] = useSelectedCompany(companies);
  // F-tarefas — quem cria (Administrador da empresa ativa / Super Admin) vê as
  // atrasadas da equipe no sino.
  const isCompanyAdmin =
    !!user &&
    companyId != null &&
    (user.isSuperAdmin ||
      user.memberships.some((m) => m.active && m.companyId === companyId && m.role === "admin"));
  const teamTasksQ = trpc.tasks.teamOverdue.useQuery(
    { companyId: companyId ?? 0 },
    { enabled: isCompanyAdmin, refetchInterval: 5 * 60_000 },
  );
  const currentCompany = companies.find((c) => c.id === companyId) ?? companies[0];

  const [coOpen, setCoOpen] = useState(false);
  const coRef = useRef<HTMLDivElement | null>(null);

  const roleReadOnly = useIsReadOnly();
  const [previewRO, setPreviewRO] = useState(false);
  const readOnly = roleReadOnly || previewRO;

  // ── Drawer de navegação no mobile/tablet (≤900px) ──
  const [navOpen, setNavOpen] = useState(false);

  // ── Busca global (dossiers por nº / placa / cliente) ──
  const [searchQ, setSearchQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const searchTerm = searchQ.trim();
  // F-search-cross-company — varre TODAS as empresas do usuário. Antes era
  // escopada na selecionada e dizia "nenhum dossier encontrado" quando o
  // dossier estava na empresa vizinha.
  const searchQuery = trpc.dossiers.searchAllCompanies.useQuery(
    { q: searchTerm, limit: 10 },
    { enabled: searchTerm.length >= 2 },
  );
  const searchResults = searchTerm.length >= 2 ? (searchQuery.data?.rows ?? []) : [];
  function goToDossier(id: number) {
    setSearchOpen(false);
    setSearchQ("");
    setLocation(`/dossiers/${id}`);
  }

  // ── Notificações (sininho): itens reais do "A fazer" da empresa ativa ──
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement | null>(null);
  const treasuryQuery = trpc.treasury.summary.useQuery(
    { companyId: companyId ?? 0 },
    { enabled: companyId != null && !grantOnly },
  );
  const bankUnrecQuery = trpc.bankTransactions.list.useQuery(
    { companyId: companyId ?? 0, reconciliationStatus: "unreconciled", limit: 1 },
    { enabled: companyId != null && !grantOnly },
  );
  // F-fatura-editada (CEO 2026-11-03) — faturas editadas no mesmo número que
  // ainda não foram reenviadas: o escritório precisa avisar o cliente.
  const unsentInvQuery = trpc.customerInvoices.unsentSummary.useQuery(
    { companyId: companyId ?? 0 },
    { enabled: companyId != null && !grantOnly },
  );

  // F-impersonate-tab — encerrar a visão: audita (logout com Bearer NÃO limpa
  // o cookie do admin), limpa o token DESTA aba e fecha-a (fallback: a aba
  // recarrega como admin, pelo cookie intacto).
  const endImpersonationMut = trpc.auth.logout.useMutation({
    onSettled: () => {
      clearImpersonationToken();
      window.close();
      setTimeout(() => window.location.assign("/dashboard"), 300);
    },
  });
  const logoutMutation = trpc.auth.logout.useMutation({
    // onSettled (sucesso OU erro): o logout só precisa limpar o cookie — o
    // servidor já manda o Set-Cookie que apaga a sessão antes de a resposta
    // ser lida. Em produção, ler essa resposta às vezes falha ("Failed to
    // fetch"), mas o cookie já foi limpo. Por isso NÃO dependemos do sucesso:
    // limpamos o cache e damos redirect forte de qualquer jeito (reload total
    // → /login). Reproduz automaticamente o "recarregar a página" que desloga.
    onSettled: () => {
      queryClient.clear();
      window.location.assign("/login");
    },
  });
  const updateLocaleMutation = trpc.auth.updateLocale.useMutation();
  function handleLocaleChange(next: Locale) {
    updateLocaleMutation.mutate({ locale: next });
  }

  // Aplica `is-readonly` no body (as páginas/é hook já escondem ações de escrita).
  useEffect(() => {
    document.body.classList.toggle("is-readonly", readOnly);
    return () => document.body.classList.remove("is-readonly");
  }, [readOnly]);

  // Fecha a busca ao clicar fora.
  useEffect(() => {
    if (!searchOpen) return;
    const handler = (e: MouseEvent) => {
      if (!searchRef.current?.contains(e.target as Node)) setSearchOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [searchOpen]);

  // Atalho ⌘K / Ctrl+K → foca a busca.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
        setSearchOpen(true);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // Fecha o drawer de navegação ao trocar de rota (clicou num item).
  useEffect(() => {
    setNavOpen(false);
  }, [location]);

  // Escape fecha o drawer de navegação.
  useEffect(() => {
    if (!navOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setNavOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [navOpen]);

  // Fecha o menu de empresas ao clicar fora.
  useEffect(() => {
    if (!coOpen) return;
    const handler = (e: MouseEvent) => {
      if (!coRef.current?.contains(e.target as Node)) setCoOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [coOpen]);

  // Fecha o menu de notificações ao clicar fora.
  useEffect(() => {
    if (!notifOpen) return;
    const handler = (e: MouseEvent) => {
      if (!notifRef.current?.contains(e.target as Node)) setNotifOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [notifOpen]);

  if (!user) return null;

  // Notificações reais (mesma fonte do painel "A fazer").
  const money0 = (v: number | string | null | undefined) => formatMoney(v, { decimals: 0 });
  const trd = treasuryQuery.data;
  const unrecCount = bankUnrecQuery.data?.total ?? 0;
  const notifItems: {
    icon: LucideIcon;
    color: string;
    title: string;
    sub: string;
    href: string;
    onClick?: () => void;
  }[] = [];
  for (const n of teamNotices) {
    notifItems.push({
      icon: Megaphone,
      color: "var(--info-500)",
      title: t("erpShell.notifNoticeTitle"),
      sub: n.message.length > 90 ? n.message.slice(0, 90) + "…" : n.message,
      href: "#",
      onClick: () => setNoticesOpen(true),
    });
  }
  // F-tarefas (CEO 2026-10-29) — minhas atrasadas / de hoje + atrasadas da equipe.
  const minhas = myTasksQ.data;
  const titulos = (bucket: "overdue" | "today") => {
    // Recorrente atrasada vários dias = uma linha "título ×N".
    const conta = new Map<string, number>();
    for (const r of minhas?.rows ?? [])
      if (r.bucket === bucket) conta.set(r.title, (conta.get(r.title) ?? 0) + 1);
    const l = [...conta].map(([ti, n]) => (n > 1 ? `${ti} ×${n}` : ti));
    const txt = l.slice(0, 2).join(" · ") + (l.length > 2 ? " …" : "");
    return txt.length > 90 ? txt.slice(0, 90) + "…" : txt;
  };
  if (minhas && minhas.overdue > 0)
    notifItems.push({
      icon: ListChecks,
      color: "var(--danger-500)",
      title: `${t("tasks.notifMineOverdue")} · ${minhas.overdue}`,
      sub: titulos("overdue"),
      href: "/tarefas",
    });
  if (minhas && minhas.today > 0)
    notifItems.push({
      icon: ListChecks,
      color: "var(--info-500)",
      title: `${t("tasks.notifMineToday")} · ${minhas.today}`,
      sub: titulos("today"),
      href: "/tarefas",
    });
  const equipe = isCompanyAdmin ? teamTasksQ.data : undefined;
  if (equipe && equipe.total > 0)
    notifItems.push({
      icon: ListChecks,
      color: "var(--danger-500)",
      title: `${t("tasks.notifTeamOverdue")} · ${equipe.total}`,
      sub: equipe.rows.map((r) => `${r.name} (${r.count})`).join(", "),
      href: "/tarefas?view=equipe",
    });
  const amendedCount = unsentInvQuery.data?.amendedCount ?? 0;
  if (amendedCount > 0)
    notifItems.push({
      icon: FilePen,
      color: "var(--warning-500)",
      title: `${amendedCount} ${t("erpShell.notifAmendedSuffix")}`,
      sub: t("erpShell.notifAmendedSub"),
      href: "/faturas?modificadas=1",
    });
  if (unrecCount > 0)
    notifItems.push({
      icon: Landmark,
      color: "var(--info-500)",
      title: `${unrecCount} ${t("erpShell.notifUnreconciledSuffix")}`,
      sub: t("nav.bankExtracts"),
      // CEO 2026-11-03 — abre só as pendentes, de todas as datas.
      href: "/banco?status=unreconciled&periodo=todos",
    });
  if (trd && Number(trd.receivable.customer.overdueTotal) > 0)
    notifItems.push({
      icon: AlertTriangle,
      color: "var(--danger-500)",
      title: t("erpShell.notifOverdueInvoices"),
      sub: `${money0(trd.receivable.customer.overdueTotal)} ${t("erpShell.notifOverdueSuffix")}`,
      href: "/faturas?status=issued",
    });
  if (trd && trd.payable.technicianCommission.dossierCount > 0)
    notifItems.push({
      icon: Wrench,
      color: "var(--accent-press)",
      title: `${trd.payable.technicianCommission.dossierCount} ${t("erpShell.notifDossiersCommissionsSuffix")}`,
      sub: `${money0(trd.payable.technicianCommission.pendingTotal)} ${t("erpShell.notifToPaySuffix")}`,
      href: "/pagamentos-tecnicos",
    });
  if (trd && trd.payable.supplier.count > 0)
    notifItems.push({
      icon: ShoppingCart,
      color: "var(--warning-500)",
      title: `${trd.payable.supplier.count} ${t("erpShell.notifSupplierInvoicesSuffix")}`,
      sub: `${money0(trd.payable.supplier.openTotal)} ${t("erpShell.notifToPaySuffix")}`,
      href: "/compras",
    });

  const label = (it: NavItem) => (it.labelKey ? t(it.labelKey) : (it.label ?? it.id));
  const activeItem = NAV.flatMap((g) => g.items).find((it) => it.id === active);
  const crumbParts = crumb ?? (activeItem ? [label(activeItem)] : []);

  const roleLabel = user.isSuperAdmin
    ? t("shell.roleSuperAdmin")
    : grantOnly
      ? t("users.roleManager")
      : user.memberships.some((m) => m.active && m.role === "admin")
        ? t("shell.roleAdmin")
        : user.memberships.some((m) => m.active && m.role === "user")
          ? t("shell.roleUser")
          : t("shell.roleViewer");

  // F-menu-unico — a MESMA regra de visibilidade para a gaveta e o trilho.
  const itemVisible = (it: NavItem): boolean => {
    if (it.everyone) return true;
    // F-acesso-por-menu — só os menus liberados em Usuários.
    if (grantOnly) {
      const menu = it.id === "dashboard" ? "painel" : it.id;
      return (menusLiberados as string[]).includes(menu);
    }
    // F-finance-viewer-access: Tesouraria → super admin OU viewer (sócio).
    if (it.financeAccess) {
      return user.isSuperAdmin || user.memberships.some((m) => m.active && m.role === "viewer");
    }
    if (it.adminOnly) {
      return user.isSuperAdmin || user.memberships.some((m) => m.active && m.role === "admin");
    }
    return !it.superAdmin || user.isSuperAdmin;
  };
  const allNavItems = NAV.flatMap((g) => g.items);
  const railItems = RAIL_IDS.map((id) => allNavItems.find((it) => it.id === id)).filter(
    (it): it is NavItem => it != null && itemVisible(it),
  );

  function chooseCompany(id: number) {
    setCoOpen(false);
    if (id === companyId) return;
    setCompanyId(id);
    // Re-escopa todas as páginas (o padrão do app para troca de empresa).
    window.location.reload();
  }

  return (
    <div className="erp">
      {/* Backdrop do drawer mobile (só renderiza quando aberto) */}
      {navOpen && <div className="erp__backdrop" onClick={() => setNavOpen(false)} aria-hidden />}

      {/* ---------------- Trilho (72 px, desktop) ---------------- */}
      <nav className="rail" data-testid="rail">
        <button
          type="button"
          className={`rail__btn${navOpen ? " on" : ""}`}
          onClick={() => setNavOpen((v) => !v)}
          aria-label={t("shell.openMenu")}
          aria-expanded={navOpen}
          data-testid="rail-menu"
        >
          <Menu size={22} />
        </button>
        {currentCompany && (
          <button
            type="button"
            className="rail__co"
            onClick={() => setNavOpen(true)}
            title={currentCompany.name}
            aria-label={currentCompany.name}
          >
            {companyInitials(currentCompany.name)}
          </button>
        )}
        <div className="rail__items">
          {railItems.map((it) => {
            const Icon = it.icon;
            const pendingN = it.id === "banco" ? unrecCount : 0;
            return (
              <Link
                key={it.id}
                href={it.href}
                className={`rail__item${it.id === active ? " active" : ""}`}
                title={label(it)}
                data-testid={`rail-${it.id}`}
              >
                <span className="rail__ico">
                  <Icon size={20} strokeWidth={1.8} />
                  {pendingN > 0 && (
                    <span className="rail__badge" title={t("shell.bankPendingBadgeTitle")}>
                      {pendingN > 99 ? "99+" : pendingN}
                    </span>
                  )}
                </span>
                <span className="rail__lbl">
                  {it.railLabelKey ? t(it.railLabelKey) : label(it)}
                </span>
              </Link>
            );
          })}
        </div>
        <Link href="/conta" className="rail__user" title={user.name} data-testid="rail-user">
          {initials(user.name)}
        </Link>
      </nav>

      {/* ---------------- Gaveta (sobre o conteúdo; aberta pelo trilho ou pelo ☰ do topo) ---------------- */}
      <aside className={`side${navOpen ? " open" : ""}`} data-testid="side-drawer">
        <div className="side__brand">
          <img src="/es-logomark.png" alt="Euro Storm" />
          <div className="wm" data-testid="app-brand">
            STORM <b>GESTION</b>
            <small>V3 · ERP</small>
          </div>
          <button
            type="button"
            className="side__close"
            onClick={() => setNavOpen(false)}
            aria-label={t("common.close")}
          >
            <X size={20} />
          </button>
        </div>

        {/* Seletor de empresa */}
        <div className="coselect" ref={coRef}>
          <button
            type="button"
            className="coselect__btn"
            onClick={() => setCoOpen((v) => !v)}
            disabled={companies.length <= 1}
          >
            <span className="coselect__av">
              {currentCompany ? companyInitials(currentCompany.name) : "—"}
            </span>
            <span style={{ minWidth: 0 }}>
              <span className="coselect__t">{currentCompany?.name ?? "—"}</span>
              <span className="coselect__s">
                {currentCompany ? companySublabel(currentCompany, t) : ""}
              </span>
            </span>
            {companies.length > 1 && <ChevronDown size={16} className="coselect__chev" />}
          </button>
          {coOpen && (
            <div className="coselect__menu">
              {companies.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`coselect__opt${c.id === companyId ? " active" : ""}`}
                  onClick={() => chooseCompany(c.id)}
                >
                  <span className="coselect__av">{companyInitials(c.name)}</span>
                  <span style={{ minWidth: 0 }}>
                    <span className="coselect__t">{c.name}</span>
                    <span className="coselect__s">{companySublabel(c, t)}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Navegação */}
        <nav className="nav">
          {/* F-acesso-por-menu — gerente de chantier no Gestion: volta ao portal dele. */}
          {grantOnly && (
            <Link
              href="/chantier"
              className="mx-2 mb-2 flex items-center justify-center rounded-lg border border-lime/40 bg-lime/10 px-2 py-1.5 text-xs font-semibold text-lime hover:bg-lime/20"
              data-testid="nav-back-to-portal"
            >
              « {t("erpShell.backToPortal")}
            </Link>
          )}
          {NAV.map((group, gi) => {
            const items = group.items.filter(itemVisible);
            if (items.length === 0) return null;
            const heading = group.sectionKey ? t(group.sectionKey) : group.section;
            return (
              <div key={gi}>
                {heading && <div className="nav__gh">{heading}</div>}
                {items.map((it) => {
                  const Icon = it.icon;
                  // F-releve-indy F3 — badge de pendências no item do banco
                  // (transações não conciliadas), como o contador da Indy.
                  const pendingN = it.id === "banco" ? (bankUnrecQuery.data?.total ?? 0) : 0;
                  return (
                    <Link
                      key={it.id}
                      href={it.href}
                      className={`nav__item${it.id === active ? " active" : ""}`}
                    >
                      <Icon size={18} strokeWidth={1.7} />
                      <span>{label(it)}</span>
                      {pendingN > 0 && (
                        <span
                          className="ml-auto rounded-full bg-amber-500 text-[10px] font-bold leading-4 text-white px-1.5"
                          title={t("shell.bankPendingBadgeTitle")}
                        >
                          {pendingN > 99 ? "99+" : pendingN}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* Rodapé do usuário */}
        <div className="side__foot">
          <div className="side__av">{initials(user.name)}</div>
          <Link
            href="/conta"
            className="side__who"
            title={user.name}
            style={{ textDecoration: "none" }}
          >
            <b>{user.name}</b>
            <span>{roleLabel}</span>
          </Link>
          <button
            type="button"
            className="side__logout"
            title={t("common.logout")}
            data-testid="logout-button"
            onClick={() => logoutMutation.mutate()}
            disabled={logoutMutation.isPending}
          >
            <LogOut size={17} />
          </button>
        </div>
      </aside>

      {/* ---------------- Main ---------------- */}
      <div className="main">
        {/* F-impersonate — sessão "Entrar como" de um super admin. */}
        {user.impersonatedBy && (
          <div
            className="flex items-center justify-center gap-3 px-4 py-1.5 text-xs font-semibold"
            style={{ background: "#D97706", color: "#fff" }}
          >
            <span>
              👁 {t("erpShell.impersonatingPre")} <b>{user.name}</b> (
              {t("erpShell.impersonatingBy")} {user.impersonatedBy.name})
            </span>
            <button
              onClick={() => endImpersonationMut.mutate()}
              disabled={endImpersonationMut.isPending}
              className="rounded border border-white/60 px-2 py-0.5 font-semibold hover:bg-white/10"
            >
              {t("erpShell.impersonatingExit")}
            </button>
          </div>
        )}
        <div className="top">
          <button
            type="button"
            className="top__menu"
            onClick={() => setNavOpen(true)}
            aria-label={t("shell.openMenu")}
          >
            <Menu size={20} />
          </button>
          <div className="crumb">
            {crumbParts.map((c, i) =>
              i === crumbParts.length - 1 ? <b key={i}>{c}</b> : <span key={i}>{c} ›</span>,
            )}
          </div>

          <div className="search-wrap" ref={searchRef}>
            <div className="search">
              <Search size={16} />
              <input
                ref={searchInputRef}
                placeholder={t("shell.searchPlaceholder")}
                value={searchQ}
                onChange={(e) => {
                  setSearchQ(e.target.value);
                  setSearchOpen(true);
                }}
                onFocus={() => setSearchOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setSearchOpen(false);
                  if (e.key === "Enter" && searchResults[0]) goToDossier(searchResults[0].id);
                }}
              />
              <span className="k">⌘K</span>
            </div>
            {searchOpen && searchTerm.length >= 2 && (
              <div className="search-menu">
                {searchQuery.isLoading && (
                  <div className="search-empty">{t("shell.searchLoading")}</div>
                )}
                {!searchQuery.isLoading && searchResults.length === 0 && (
                  <div className="search-empty">{t("shell.searchEmpty")}</div>
                )}
                {searchResults.map((d) => (
                  <button
                    type="button"
                    key={d.id}
                    className="search-item"
                    onClick={() => goToDossier(d.id)}
                  >
                    <b>
                      {d.customerInvoiceNumber ??
                        d.invoiceNumberExternal ??
                        d.dossierCode ??
                        `#${d.id}`}
                    </b>
                    <span>
                      {[d.plate, d.customerName].filter(Boolean).join(" · ") || "—"}
                      {/* Marca a empresa quando o resultado está FORA da
                          selecionada — é o caso que antes sumia da busca. */}
                      {d.companyId !== companyId && d.companyName ? (
                        <em
                          style={{
                            marginLeft: 6,
                            fontStyle: "normal",
                            padding: "1px 6px",
                            borderRadius: 999,
                            border: "1px solid var(--warning-500)",
                            color: "var(--warning-500)",
                            fontSize: 10.5,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {d.companyName}
                        </em>
                      ) : null}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="top__right">
            <PinChip />
            {readOnly && (
              <span className="robadge">
                <Eye size={13} /> {t("shell.readOnly")}
              </span>
            )}
            <LanguageSwitcher size="sm" onChange={handleLocaleChange} />
            <button
              type="button"
              className="iconbtn"
              title={theme === "dark" ? t("shell.themeLight") : t("shell.themeDark")}
              aria-label={theme === "dark" ? t("shell.themeLight") : t("shell.themeDark")}
              onClick={toggleTheme}
            >
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button
              type="button"
              className={`iconbtn iconbtn--ro${readOnly ? " on" : ""}`}
              title={t("shell.readOnly")}
              onClick={() => !roleReadOnly && setPreviewRO((v) => !v)}
            >
              <Eye size={18} />
            </button>
            <div className="notif-wrap" ref={notifRef}>
              <button
                type="button"
                className={`iconbtn${notifItems.length > 0 ? " alert" : ""}`}
                title={
                  notifItems.length > 0
                    ? `${notifItems.length} ${t("shell.notifications")}`
                    : t("shell.notifications")
                }
                aria-label={t("shell.notifications")}
                onClick={() => setNotifOpen((v) => !v)}
              >
                <Bell size={18} />
                {notifItems.length > 0 && <span className="dot">{notifItems.length}</span>}
              </button>
              {notifOpen && (
                <div className="notif-menu">
                  <div className="notif-head">
                    {t("shell.notifications")}
                    <span>{notifItems.length}</span>
                  </div>
                  {notifItems.length === 0 && (
                    <div className="notif-empty">{t("erpShell.notifEmpty")}</div>
                  )}
                  {notifItems.map((n, i) => {
                    const NIcon = n.icon;
                    return (
                      <button
                        key={i}
                        type="button"
                        className="notif-item"
                        onClick={() => {
                          setNotifOpen(false);
                          if (n.onClick) n.onClick();
                          else setLocation(n.href);
                        }}
                      >
                        <span className="notif-ic" style={{ color: n.color }}>
                          <NIcon size={16} />
                        </span>
                        <span style={{ minWidth: 0 }}>
                          <b>{n.title}</b>
                          <span>{n.sub}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="content">{children}</div>
      </div>
      <TeamNoticesPopup
        notices={teamNotices}
        tasks={popupTasks}
        open={noticesOpen}
        onClose={() => setNoticesOpen(false)}
      />
    </div>
  );
}

/**
 * F-pin (CEO 2026-09-19) — alfinete global: cliente/chantier fixado filtra
 * as pesquisas (Agenda, Dossiers) até soltar. Sem cadeado — é um 📌.
 */
function PinChip() {
  const t = useT();
  const pin = usePin();
  if (!pin) return null;
  return (
    <button
      type="button"
      onClick={() => setPin(null)}
      title={t("erpShell.unpinTitle")}
      className="pinchip"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        border: "1px solid var(--accent, #84cc16)",
        borderRadius: 8,
        padding: "4px 9px",
        fontSize: 12,
        fontWeight: 600,
        background: "transparent",
        color: "inherit",
        cursor: "pointer",
        maxWidth: 210,
      }}
    >
      📌{" "}
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {pin.label}
      </span>{" "}
      ✕
    </button>
  );
}
